import { useQuery } from '@tanstack/react-query';
import { FC, useState } from 'react';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import { Controls, ControlsButton } from '@/components/Controls';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import {
  ChannelGroupResource,
  DQCascadeGroupResource,
  DQCascadeRunResource,
} from '@/types/resources';
import { api } from '@/utils/api';

import ItemsTable from './ItemsTable';
import RunsTable from './RunsTable';
import {
  RUNNING_REFETCH_INTERVAL,
  RunStatusBadge,
  dqCascadeQueryKeys,
  formatDate,
  formatRunWindow,
} from './helpers';

const Dialog = styled(StyledDialog)`
  width: 96%;
  max-width: 1160px;
  text-align: left;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  height: 72px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const StyledCloseButton = styled(CloseButton)`
  position: static;
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 2fr;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const SummaryItem = styled.div`
  padding: 16px 24px;
  &:not(:last-child) {
    border-right: 1px solid ${({ theme }) => theme.colors.lightGray};
  }
`;

const SummaryValue = styled(Text).attrs({ $type: 'button', as: 'div' })`
  margin-top: 4px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const Message = styled(Text).attrs({ $color: 'gray' })`
  padding: 48px 24px;
  text-align: center;
`;

const CloseIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13 1L1 13"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13 13L1 1"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

enum Tabs {
  RUNS = 'runs',
  ITEMS = 'items',
}

interface Props {
  group: ChannelGroupResource;
  groupNumber: number;
  onClose: VoidFunction;
}

const DQCascadeModal: FC<Props> = ({ group, groupNumber, onClose }) => {
  const [activeTab, setActiveTab] = useState(Tabs.RUNS);
  const [selectedRun, setSelectedRun] = useState<DQCascadeRunResource | null>(
    null,
  );

  const { data: cascadeGroups, isLoading } = useQuery(
    dqCascadeQueryKeys.groups(),
    () =>
      api.get<never, DQCascadeGroupResource[]>(
        '/v1/editorial/queue/dq-cascade/groups',
      ),
    {
      refetchInterval: (data) =>
        data?.some((cascade) => cascade.latestRun?.status === 'running')
          ? RUNNING_REFETCH_INTERVAL
          : false,
    },
  );

  const cascade = cascadeGroups?.find(
    (cascade) => cascade.groupNumber === groupNumber,
  );

  const latestRun = cascade?.latestRun;

  const showRunItems = (run: DQCascadeRunResource) => {
    setSelectedRun(run);
    setActiveTab(Tabs.ITEMS);
  };

  const tabs = (
    <Controls>
      <ControlsButton
        active={activeTab === Tabs.RUNS}
        onClick={() => setActiveTab(Tabs.RUNS)}
      >
        Runs
      </ControlsButton>
      <ControlsButton
        active={activeTab === Tabs.ITEMS}
        onClick={() => setActiveTab(Tabs.ITEMS)}
      >
        Items
      </ControlsButton>
    </Controls>
  );

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Header>
          <Text $type="button">{group.name} / Cascade status</Text>
          <StyledCloseButton onClick={onClose}>
            <CloseIcon />
          </StyledCloseButton>
        </Header>
        {isLoading ? (
          <Message>Loading data...</Message>
        ) : !cascade ? (
          <Message>
            {group.name} is not a scheduled cascade group. Only groups named “DQ
            Cascade 1” to “DQ Cascade 12” are distributed.
          </Message>
        ) : (
          <>
            <Summary>
              <SummaryItem>
                <Text $type="smallBold" $color="gray">
                  Delay
                </Text>
                <SummaryValue>{cascade.delayDays} days</SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <Text $type="smallBold" $color="gray">
                  Watermark
                </Text>
                <SummaryValue
                  title={
                    cascade.watermark
                      ? 'Main page posts published up to and including this day have been distributed'
                      : undefined
                  }
                >
                  {cascade.watermark
                    ? formatDate(cascade.watermark)
                    : 'Not started'}
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <Text $type="smallBold" $color="gray">
                  Latest run
                </Text>
                <SummaryValue>
                  {latestRun ? (
                    <>
                      {formatDate(latestRun.runDate)}
                      <RunStatusBadge run={latestRun} />
                      <Text as="span" $type="smallMedium" $color="gray">
                        {latestRun.postsCount} posts (
                        {formatRunWindow(latestRun)}) to {latestRun.pagesCount}{' '}
                        pages · {latestRun.itemCounts.inserted} inserted ·{' '}
                        {latestRun.itemCounts.skipped} skipped
                      </Text>
                    </>
                  ) : (
                    'No runs yet · runs daily at 05:00'
                  )}
                </SummaryValue>
              </SummaryItem>
            </Summary>
            {activeTab === Tabs.RUNS ? (
              <RunsTable
                controls={tabs}
                groupNumber={groupNumber}
                onShowItems={showRunItems}
              />
            ) : (
              <ItemsTable
                controls={tabs}
                group={group}
                groupNumber={groupNumber}
                run={selectedRun}
                onClearRun={() => setSelectedRun(null)}
              />
            )}
          </>
        )}
      </Dialog>
    </Modal>
  );
};

export default DQCascadeModal;
